import { RovingFocusGroup } from "@tamagui/roving-focus";
import { Image, ScrollView, SizableText, XStack, YStack, useMedia, View, isWeb, styled, useWindowDimensions } from "tamagui";
import { Drawer } from "../common/Drawer.mjs";
import { Link as RRLink, useNavigate } from "react-router-dom";
import { ActionButton } from "../../../UtilComponents/index.mjs";
import { ChevronRight, Plus, Minus } from "@tamagui/lucide-icons-2";
import { useEffect, useState } from "react";
import { getImageColorStyle, usePathname } from "@my/util";
import { defaultTo } from "lodash";
import { jsx, jsxs } from "react/jsx-runtime";
function LinkNav({
  to,
  linkText,
  subText,
  icon,
  externalAnchor,
  active,
  innerRef,
  otherLinkProps,
  subItems,
  isExpanded,
  isSubItem,
  ...props
}) {
  const navigate = useNavigate();
  const childrenRootProps = Object.assign({}, props);
  if (active) {
    childrenRootProps["active"] = true;
    childrenRootProps["borderRadius"] = 15;
    childrenRootProps["backgroundColor"] = "rgb(150, 206, 172) !important";
  }
  if (innerRef) {
    childrenRootProps["ref"] = innerRef;
  }
  const otherLinkPropsObj = {
    ...otherLinkProps,
    style: {
      textDecoration: "none"
    }
  };
  if (isSubItem) {
    otherLinkPropsObj.style["backgroundColor"] = "rgb(148, 227, 169)";
  }
  const hasSubItems = subItems?.length;
  if (hasSubItems) {
    otherLinkPropsObj.style["cursor"] = "pointer";
  }
  const rightIcon = hasSubItems ? isExpanded ? /* @__PURE__ */jsx(Minus, {
    size: 20,
    color: "white"
  }) : /* @__PURE__ */jsx(Plus, {
    size: 20,
    color: "white"
  }) : /* @__PURE__ */jsx(ChevronRight, {
    size: 20,
    color: "white"
  });
  otherLinkPropsObj["onClick"] = () => {
    window.scrollTo(0, 0);
    if (to) {
      navigate(to);
    }
    if (props.onPress) {
      props.onPress();
    }
  };
  if (hasSubItems) {}
  const children = /* @__PURE__ */jsxs(XStack, {
    ...(childrenRootProps || {}),
    px: "$4",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    children: [/* @__PURE__ */jsx(Image, {
      src: icon,
      tintColor: "#fff",
      style: {
        height: 22,
        width: 22,
        ...getImageColorStyle("#fff")
      },
      resizeMode: "contain"
    }), /* @__PURE__ */jsxs(YStack, {
      flex: 1,
      children: [/* @__PURE__ */jsx(SizableText, {
        unstyled: true,
        color: "#fff",
        size: 15,
        fontWeight: "600",
        children: linkText
      }), /* @__PURE__ */jsx(SizableText, {
        unstyled: true,
        color: "$color10",
        size: "$3",
        children: subText || ""
      })]
    }), /* @__PURE__ */jsx(ActionButton, {
      unstyled: true,
      height: 18,
      width: 18,
      icon: rightIcon
    })]
  });
  return externalAnchor ? /* @__PURE__ */jsx("a", {
    style: {
      ...otherLinkPropsObj.style
    },
    href: to,
    target: otherLinkPropsObj?.target || "_blank",
    rel: otherLinkPropsObj?.rel || "noopener noreferrer",
    children
  }) : hasSubItems ? /* @__PURE__ */jsx("div", {
    ...(otherLinkPropsObj || {}),
    children
  }) : /* @__PURE__ */jsx(RRLink, {
    ...(otherLinkPropsObj || {}),
    children
  });
}
const Link = styled(LinkNav, {
  tag: "a",
  textTransform: "none",
  display: "flex",
  paddingVertical: "$3",
  marginBottom: 5,
  borderColor: "transparent",
  hoverStyle: {
    backgroundColor: "rgba(150, 206, 172, .7)",
    borderRadius: 15
  }
});
const FullSideBarControlled = props => {
  const media = useMedia();
  return media["max-sm"] ? /* @__PURE__ */jsx(FloatingSideBar, {
    ...props
  }) : /* @__PURE__ */jsx(Sidebar, {
    ...props
  });
};
const Sidebar = ({
  navItems,
  open,
  ...rest
}) => {
  const {
    width: windowWidth,
    height
  } = useWindowDimensions();
  const isSmallerWindow = windowWidth <= 600;
  const top = defaultTo(rest.top, "90px");
  return /* @__PURE__ */jsx(YStack, {
    width: 300,
    height,
    backgroundColor: "#85C19D",
    transition: "lazy",
    style: {
      position: "sticky",
      top,
      alignSelf: "flex-start"
    },
    ...(open ? {} : {
      display: "none"
    }),
    children: /* @__PURE__ */jsx(SideBarContent, {
      ...{
        navItems
      }
    })
  });
};
const FloatingSideBar = ({
  open,
  setOpen,
  navItems,
  top
}) => {
  const {
    height,
    width
  } = useWindowDimensions();
  const pathname = usePathname();
  useEffect(() => {
    if (open) {
      setOpen(false);
    }
  }, [pathname]);
  return /* @__PURE__ */jsx(View, {
    flexDirection: "column",
    ...(isWeb && {
      onKeyDown: e => {
        if (e.key === "Escape") {
          setOpen(false);
        }
      }
    }),
    children: /* @__PURE__ */jsxs(Drawer, {
      open,
      onOpenChange: setOpen,
      children: [/* @__PURE__ */jsx(Drawer.Overlay, {
        height,
        width,
        transition: void 0,
        zIndex: 499,
        position: "fixed",
        top
      }), /* @__PURE__ */jsx(Drawer.Swipeable, {
        position: "fixed",
        children: /* @__PURE__ */jsx(Drawer.Content, {
          height,
          transition: void 0,
          width: 300,
          backgroundColor: "#85C19D",
          children: /* @__PURE__ */jsx(SideBarContent, {
            width: 300,
            ...{
              navItems
            }
          })
        })
      })]
    })
  });
};
const SideBarContent = View.styleable(({
  navItems,
  navItemOnPress,
  ...props
}, ref) => {
  const navItemsArray = navItems;
  const onPress = navItemOnPress ? {
    onPress: navItemOnPress
  } : {};
  const [expandedState, setExpandedState] = useState({});
  const toggleExpanded = idx => {
    setExpandedState(currState => {
      currState[idx] = !currState[idx];
      return {
        ...currState
      };
    });
  };
  return /* @__PURE__ */jsx(ScrollView, {
    ref,
    backgroundColor: "#85C19D",
    flex: 1,
    paddingHorizontal: 5,
    ...props,
    children: /* @__PURE__ */jsx(RovingFocusGroup, {
      children: navItemsArray.filter(item => !item.disabled).map((item, index) => item.subItems ? [/* @__PURE__ */jsx(RovingFocusGroup.Item, {
        focusable: true,
        children: /* @__PURE__ */jsx(NavLink, {
          href: "#",
          ...item,
          onPress: () => toggleExpanded(index),
          isExpanded: expandedState[index]
        })
      }, index), ...(expandedState[index] ? item.subItems.map((subItem, subIdx) => /* @__PURE__ */jsx(RovingFocusGroup.Item, {
        focusable: true,
        children: /* @__PURE__ */jsx(NavLink, {
          href: "#",
          ...subItem,
          ...onPress,
          isSubItem: true
        })
      }, subIdx + "" + index)) : [])] : /* @__PURE__ */jsx(RovingFocusGroup.Item, {
        focusable: true,
        children: /* @__PURE__ */jsx(NavLink, {
          href: "#",
          ...item,
          ...onPress
        })
      }, index)).flat()
    })
  });
});
const NavLink = Link.styleable(({
  children,
  href = "#",
  onPress,
  ...rest
}, ref) => {
  const pathname = usePathname();
  const isActive = rest.to === pathname;
  return /* @__PURE__ */jsx(Link, {
    innerRef: ref,
    onPress,
    href,
    active: isActive,
    group: "navLink",
    draggable: false,
    ...rest
  });
});
export { FullSideBarControlled };
//# sourceMappingURL=FullSideBarControlled.mjs.map
